'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, TrendingUp, Clock, Target, Lightbulb, Star, Eye, Brain, Heart, ArrowRight, Scale, CheckCircle, XCircle } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function KararVermePage() {
  const cardPositions = [
    { position: 1, name: "Mevcut Durum", description: "Karar vereceğiniz konudaki şu anki durumunuz", side: "center" },
    { position: 2, name: "Seçenek A", description: "Birinci seçeneğin genel enerjisi", side: "left" },
    { position: 3, name: "Seçenek B", description: "İkinci seçeneğin genel enerjisi", side: "right" },
    { position: 4, name: "A'nın Avantajları", description: "Birinci seçeneği seçmenin olumlu yanları", side: "left" },
    { position: 5, name: "B'nin Avantajları", description: "İkinci seçeneği seçmenin olumlu yanları", side: "right" },
    { position: 6, name: "Sonuç Rehberliği", description: "Hangi yolu seçmeniz gerektiğine dair rehberlik", side: "center" }
  ];

  const decisionTypes = [
    {
      category: "Kariyer Kararları",
      examples: [
        "İş değiştirmek vs. Mevcut işte kalmak",
        "Yeni pozisyon vs. Güvenli durum",
        "Girişimcilik vs. Çalışan kalmak",
        "Eğitime devam vs. İş hayatına başlama"
      ]
    },
    {
      category: "İlişki Kararları",
      examples: [
        "İlişkiye devam vs. Ayrılık",
        "Evlenmek vs. Beklemek",
        "Uzaktan ilişki vs. Yakın çevre",
        "Bağışlamak vs. Sınır koymak"
      ]
    },
    {
      category: "Yaşam Kararları",
      examples: [
        "Taşınmak vs. Yerinde kalmak",
        "Yatırım yapmak vs. Para biriktirmek",
        "Sağlık programı A vs. Program B",
        "Hobi geliştirme vs. Dinlenme"
      ]
    }
  ];

  const interpretationGuide = [
    {
      comparison: "Pozitif vs Negatif Kartlar",
      guidance: "Hangi tarafta daha çok olumlu kart var? Bu bir işaret olabilir.",
      icon: <CheckCircle className="h-5 w-5" />
    },
    {
      comparison: "Element Analizi",
      guidance: "Ateş kartları eylem, Su kartları duygu, Hava kartları düşünce, Toprak kartları pratiklik gösterir.",
      icon: <Star className="h-5 w-5" />
    },
    {
      comparison: "Sayı Enerjileri",
      guidance: "Düşük sayılar başlangıç, orta sayılar gelişim, yüksek sayılar tamamlanma enerjisi taşır.",
      icon: <Scale className="h-5 w-5" />
    },
    {
      comparison: "Major vs Minor Arkana",
      guidance: "Major Arkana kartları daha derin, kadersel etkiler; Minor Arkana günlük pratik sonuçlar gösterir.",
      icon: <Eye className="h-5 w-5" />
    }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Okuma Teknikleri</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Karar Verme Yayılımı</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/interpretation-techniques"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Okuma Teknikleri
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-yellow-500/20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-100">Karar Verme Yayılımı</h1>
              <p className="text-yellow-300 font-medium">Orta Seviye • 6 Kart</p>
            </div>
          </div>
          <p className="text-lg text-slate-200 leading-relaxed">
            İki seçenek arasında karşılaştırma yaparak karar vermenize yardımcı olur.
            Önemli yaşam kararları için ideal olan bu yöntem, her seçeneğin avantajlarını objektif olarak değerlendirir.
          </p>
        </div>

        {/* Ana Bilgiler */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="h-6 w-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-slate-100">Süre</h3>
            </div>
            <p className="text-slate-300">15-25 dakika</p>
            <p className="text-slate-400 text-sm">Karşılaştırmalı analiz</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-slate-100">Amaç</h3>
            </div>
            <p className="text-slate-300">Karar verme</p>
            <p className="text-slate-400 text-sm">Seçenek karşılaştırması</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Lightbulb className="h-6 w-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-slate-100">Seviye</h3>
            </div>
            <p className="text-slate-300">Orta</p>
            <p className="text-slate-400 text-sm">Objektif değerlendirme</p>
          </div>
        </div>

        {/* Karar Verme Dizilimi */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Karar Verme Dizilimi</h2>
          <div className="bg-slate-800 rounded-xl p-8 border border-slate-700">
            <div className="flex justify-center">
              <div className="grid grid-cols-3 gap-6 max-w-lg">
                {/* Row 1 */}
                <div className="bg-blue-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  2<br/><span className="text-xs">Seçenek A</span>
                </div>
                <div className="bg-purple-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  1<br/><span className="text-xs">Mevcut<br/>Durum</span>
                </div>
                <div className="bg-pink-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  3<br/><span className="text-xs">Seçenek B</span>
                </div>

                {/* Row 2 */}
                <div className="bg-green-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  4<br/><span className="text-xs">A'nın<br/>Avantajları</span>
                </div>
                <div className="bg-orange-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  6<br/><span className="text-xs">Sonuç<br/>Rehberliği</span>
                </div>
                <div className="bg-teal-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  5<br/><span className="text-xs">B'nin<br/>Avantajları</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Kart Pozisyonları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Kart Pozisyonları Detayı</h2>
          <div className="space-y-4">
            {cardPositions.map((card) => (
              <div key={card.position} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{card.position}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {card.side === "left" && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}
                    {card.side === "right" && <div className="w-3 h-3 bg-pink-500 rounded-full"></div>}
                    {card.side === "center" && <div className="w-3 h-3 bg-purple-500 rounded-full"></div>}
                    <h3 className="text-lg font-semibold text-slate-100">{card.name}</h3>
                  </div>
                </div>
                <p className="text-slate-300 text-sm ml-14">{card.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Karar Türleri */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Uygun Karar Türleri</h2>
          <div className="space-y-6">
            {decisionTypes.map((category, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                  <Scale className="h-5 w-5 text-yellow-400" />
                  {category.category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {category.examples.map((example, exIndex) => (
                    <div key={exIndex} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full flex-shrink-0"></div>
                      <p className="text-slate-300 text-sm">{example}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Karşılaştırma Yöntemleri */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Karşılaştırma Yöntemleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {interpretationGuide.map((guide, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center">
                    {guide.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-100">{guide.comparison}</h3>
                </div>
                <p className="text-slate-300">{guide.guidance}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Okuma Süreci */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Adım Adım Okuma Süreci</h2>
          <div className="space-y-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Seçenekleri Netleştirin</h3>
                  <p className="text-slate-300 mb-2">
                    Karar vereceğiniz iki seçeneği net bir şekilde tanımlayın.
                    Seçenekler birbirinden mümkün olduğunca farklı olmalı.
                  </p>
                  <div className="bg-slate-700 rounded-lg p-3">
                    <p className="text-slate-400 text-sm italic">
                      Örnek: "İş değiştirmek" vs "Mevcut işte kalmak"
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Kartları Sırayla Çekin</h3>
                  <p className="text-slate-300 mb-2">
                    İlk önce 1 numaralı kartı (mevcut durum) çekin ve yorumlayın.
                    Sonra 2 ve 3 numaralı kartları (seçenekleri) yan yana yerleştirin.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Avantajları Değerlendirin</h3>
                  <p className="text-slate-300 mb-2">
                    4 ve 5 numaralı kartları çekerek her seçeneğin avantajlarını inceleyin.
                    Bu kartlar objektif bir karşılaştırma yapmanıza yardımcı olur.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Son Rehberliği Alın</h3>
                  <p className="text-slate-300">
                    6 numaralı kart (sonuç rehberliği) hangi yolu seçmeniz gerektiğine dair
                    genel bir yönlendirme verir. Bu kartı diğer tüm kartlarla birlikte değerlendirin.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dikkat Edilmesi Gerekenler */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Dikkat Edilmesi Gerekenler</h2>
          <div className="space-y-4">
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3 flex items-center gap-2">
                <Brain className="h-5 w-5" />
                Objektif Kalın
              </h3>
              <p className="text-amber-100">
                Kendi tercihlerinizi kartlara yansıtmamaya çalışın. Her kartı kendi anlamında değerlendirin.
              </p>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5" />
                Duygularınızı da Dinleyin
              </h3>
              <p className="text-amber-100">
                Kartlar mantıklı bir seçeneği gösterse bile, duygusal olarak hazır hissetmiyorsanız zamanlama konusunu düşünün.
              </p>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Kesin Hüküm Vermekten Kaçının
              </h3>
              <p className="text-amber-100">
                Tarot rehberlik verir, kesin emirler vermez. Son karar her zaman sizin sorumluluğunuzdadır.
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/interpretation-techniques/iliski-yayilimi"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: İlişki Yayılımı
          </Link>

          <Link
            href="/about/tarot/interpretation-techniques/astrolojik-yayilim"
            className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Astrolojik Yayılım
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}